import { useEffect, useState } from "react";
import { Check, Flame, X } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { portalApi, portalErr } from "./portalApi";
import "../test/testTheme.css";

function DailyInner() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    portalApi.get("/portal/daily").then(({ data: d }) => setData(d)).catch((err) => setError(portalErr(err)));
    portalApi.get("/portal/daily/history").then(({ data: d }) => setHistory(d)).catch(() => {});
  };
  useEffect(load, []);

  const answer = async (opt) => {
    setBusy(true);
    setError("");
    try {
      const { data: result } = await portalApi.post("/portal/daily/answer", { selected: opt, question_id: data.question.question_id });
      setData((c) => ({ ...c, attempt: result, streak: result.streak }));
      portalApi.get("/portal/daily/history").then(({ data: d }) => setHistory(d)).catch(() => {});
    } catch (err) { setError(portalErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="tp-wrap" style={{ maxWidth: 760, padding: "44px 22px 70px" }}>
      <div className="tp-eyebrow">DAILY QUESTION · {data?.date}</div>
      <h1 className="tp-h2" style={{ fontSize: 32 }} data-testid="daily-title">Question of the Day</h1>
      {history && <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "10px 0 18px" }} data-testid="daily-stats">
        <span className="tp-pill warn" data-testid="daily-streak"><Flame size={14} /> {history.streak}-day streak</span>
        <span className="tp-pill info" data-testid="daily-record">{history.correct}/{history.total} correct all time</span>
      </div>}
      {error && <p className="tp-error" role="alert" data-testid="daily-error">{error}</p>}
      {!data && <p className="tp-muted">Loading today&apos;s question…</p>}
      {data && !data.question && <div className="tp-panel" style={{ textAlign: "center" }} data-testid="daily-none"><p className="tp-muted">{data.message}</p></div>}
      {data?.question && (
        <div className="tp-qcard" data-testid="daily-card">
          <div className="tp-qmeta">{data.question.topic && <span className="tp-pill info">{data.question.topic.toUpperCase()}</span>}{data.question.difficulty && <span className="tp-pill warn">{data.question.difficulty.toUpperCase()}</span>}</div>
          <h3 data-testid="daily-question-text">{data.question.text}</h3>
          {data.question.image_url && <img src={data.question.image_url} alt="Question diagram" />}
          {["A", "B", "C", "D"].map((key) => {
            const attempted = !!data.attempt;
            const isCorrectKey = attempted && data.attempt.correct_answer === key;
            const isPicked = attempted && data.attempt.selected === key;
            const cls = attempted ? (isCorrectKey ? "tp-option selected" : isPicked ? "tp-option" : "tp-option") : "tp-option";
            const style = attempted ? { cursor: "default", ...(isCorrectKey ? { borderColor: "#12b76a", background: "#ecfdf3" } : isPicked && !data.attempt.correct ? { borderColor: "#d92d20", background: "#fef3f2" } : {}) } : {};
            return <button type="button" key={key} disabled={attempted || busy} className={cls} style={style} onClick={() => answer(key)} data-testid={`daily-option-${key}`}><span className="key">{key}</span> {data.question.options[key]} {attempted && isCorrectKey && <Check size={15} />} {attempted && isPicked && !data.attempt.correct && <X size={15} />}</button>;
          })}
          {data.attempt && (
            <div style={{ marginTop: 16 }} data-testid="daily-feedback">
              <p className={data.attempt.correct ? "tp-pill ok" : "tp-pill bad"} data-testid="daily-verdict">{data.attempt.correct ? "Correct! Well done." : `Not quite — the correct answer is ${data.attempt.correct_answer}.`}</p>
              {data.attempt.explanation && <div className="tp-expl"><b>Explanation</b><br />{data.attempt.explanation}</div>}
              {data.attempt.solution && <div className="tp-expl"><b>Detailed solution</b><br />{data.attempt.solution}</div>}
              <p className="tp-note" style={{ marginTop: 10 }}>Come back tomorrow for the next question — keep your streak alive.</p>
            </div>
          )}
        </div>
      )}
      {history && history.history.length > 0 && (
        <section className="tp-panel" style={{ marginTop: 22 }} data-testid="daily-history">
          <h2 className="tp-h2" style={{ fontSize: 18 }}>Recent daily questions</h2>
          {history.history.slice(0, 10).map((h, i) => (
            <div key={`${h.date}-${i}`} className="tp-history-row" data-testid={`daily-history-${i}`}>
              <span className="tp-note" style={{ width: 90 }}>{h.date}</span>
              <span style={{ flex: 1, fontSize: 13 }}>{h.question_text.slice(0, 80)}{h.question_text.length > 80 ? "…" : ""}</span>
              <span className={h.correct ? "tp-pill ok" : "tp-pill bad"}>{h.correct ? "CORRECT" : "WRONG"}</span>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

export default function PortalDaily() {
  return <PortalShell><DailyInner /></PortalShell>;
}
